// src/components/kuestion/student/ExamRunner.jsx
//
// An assigned sitting IS an exam session (backend response A2):
//   POST /campus/my/assignments/{id}/start → {session_id, questions, time_limit, seconds_remaining, mode}
//   POST /kuestion/exams/{session_id}/submit with the STUDENT token
// The clock is server-authoritative (A3): `seconds_remaining` is displayed
// and counted down, never extended or reconciled against the device clock.
// A proctored sitting can be left and resumed while the window is open and
// the time limit has not elapsed (A4); if it runs out, the server submits it
// with whatever was answered and reports `sitting_expired`.
//
// The submit response decides what comes next (§6): a `results` array means
// self-assessment → review; otherwise the proctored receipt, which renders
// nothing but what the payload carries.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { useStudentAuth } from '../../../context/StudentAuthContext';
import { useStudentApi } from '../useKuestionApi';
import { ThemeToggle } from '../shared/ThemeContext';
import AppNoticeboard from '../shared/AppNoticeboard';
import { Loading, Alert, Mark, Dialog } from '../shared/Bits';
import { FlagIcon, CheckIcon, XIcon, WarnIcon } from '../shared/Icons';
import { fmtClock, fmtDateTimeZ, isProctored } from '../shared/format';
import { kq, storageKey } from '../../../config/routes';
import { normaliseAssignments } from './assignmentShape';
import useAnswerSaver, { clearSeq, seedSeq, SAVE_LABEL } from '../shared/useAnswerSaver';
import { noteOpenSitting, clearOpenSitting } from '../shared/openSitting';

const LETTERS = 'ABCDEFGHIJ';
const storeKey = (assignmentId) => storageKey('kuestion', `sitting.${assignmentId}`);

// Options arrive as "A. text" strings today; as {letter, text} once Q6 lands.
const optionOf = (opt, i) => {
  if (opt && typeof opt === 'object') return { letter: opt.letter || LETTERS[i], text: opt.text ?? '' };
  const s = String(opt ?? '');
  const m = s.match(/^([A-Z])[.)]\s*(.*)$/s);
  return m ? { letter: m[1], text: m[2] } : { letter: LETTERS[i], text: s };
};

export default function ExamRunner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const api = useStudentApi();
  const { student, logout } = useStudentAuth();

  const [assignment, setAssignment] = useState(location.state?.assignment || null);
  const [phase, setPhase] = useState('starting'); // starting | running | submitting | receipt | results | blocked | expired
  const [error, setError] = useState('');
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState(() => new Set());
  const [cur, setCur] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null); // null = untimed
  const [outcome, setOutcome] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const submitting = useRef(false);
  const startedAt = useRef(Date.now());

  const proctored = isProctored(session?.mode ?? assignment?.mode);

  // ── start (or resume) ─────────────────────────────────────────────
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        if (!assignment) {
          const list = await api.get(API_ENDPOINTS.CAMPUS_MY_ASSIGNMENTS);
          const a = normaliseAssignments(list).find((x) => String(x.id) === String(id));
          if (live && a) setAssignment(a);
        }
        const d = await api.postJson(API_ENDPOINTS.CAMPUS_MY_ASSIGNMENT_START(id), {});
        if (!live) return;
        // answers_so_far (item 3): what the server holds for a resumed sitting,
        // so another device is not a blank page. Array or map, either is read.
        const soFar = {};
        const rawSoFar = d.answers_so_far;
        (Array.isArray(rawSoFar) ? rawSoFar : Object.entries(rawSoFar || {}).map(([qid, a]) => (a && typeof a === 'object' ? { question_id: qid, ...a } : { question_id: qid, user_answer: a })))
          .forEach((a) => { if (a && a.question_id != null) soFar[a.question_id] = { user_answer: a.user_answer || '', is_flagged: Boolean(a.is_flagged) }; });
        d.answers_so_far_map = soFar;
        if (d.sitting_id != null) seedSeq(`sitting.${d.sitting_id}`, d.max_seq);
        setSession(d);
        // Answers are mirrored locally so a reload does not lose them; the
        // clock is NOT — it comes from the server every time.
        let restored = null;
        try { restored = JSON.parse(localStorage.getItem(storeKey(id)) || 'null'); } catch { /* ignore */ }
        const sid = d.sitting_id ?? d.session_id;
        if (restored && sid != null && restored.session_id === sid) {
          setAnswers(restored.answers || {});
          setFlagged(new Set(restored.flagged || []));
        } else if (Object.keys(soFar).length) {
          const a = {}; const fl = [];
          d.questions?.forEach((qq) => { const v = soFar[qq.id]; if (v?.user_answer) a[qq.id] = v.user_answer; if (v?.is_flagged) fl.push(qq.id); });
          setAnswers(a); setFlagged(new Set(fl));
        }
        startedAt.current = Date.now();
        const secs = d.seconds_remaining != null ? Number(d.seconds_remaining)
          : Number(d.time_limit_seconds || 0) > 0 ? Number(d.time_limit_seconds)
            : (Number(d.time_limit || 0) > 0 ? Number(d.time_limit) * 60 : null);
        setTimeLeft(secs != null && Number.isFinite(secs) ? Math.max(0, secs) : null);
        setPhase('running');
      } catch (e) {
        if (!live) return;
        setError(e.message);
        setPhase(e.reason === 'sitting_expired' ? 'expired' : 'blocked');
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, id]);

  useEffect(() => {
    if (!session) return;
    try { localStorage.setItem(storeKey(id), JSON.stringify({ session_id: session.sitting_id ?? session.session_id, answers, flagged: [...flagged] })); } catch { /* private mode */ }
  }, [session, answers, flagged, id]);

  // ── save as you go ────────────────────────────────────────────────
  // v4 says an assigned sitting is an exam session; the item 3 schema gives
  // it its own sitting id and save route. Follow whichever id the start
  // response carries until that is settled.
  const sittingId = session?.sitting_id;
  const sessionId = session?.session_id;
  const answerUrl = useCallback((qid) => (sittingId != null
    ? API_ENDPOINTS.CAMPUS_MY_SITTING_ANSWER(sittingId, qid)
    : API_ENDPOINTS.KQ_EXAM_ANSWER(sessionId, qid)), [sittingId, sessionId]);
  const sittingKey = sittingId != null ? `sitting.${sittingId}` : sessionId != null ? `session.${sessionId}` : null;
  const saveState = useAnswerSaver({
    api, urlFor: sittingKey ? answerUrl : null, sittingKey, answers, flagged, enabled: phase === 'running',
    baseline: session?.answers_so_far_map,
  });

  // ── submit ────────────────────────────────────────────────────────
  const submit = useCallback(async () => {
    if (submitting.current || !session) return;
    submitting.current = true;
    setConfirm(false);
    setPhase('submitting');
    setError('');
    const list = (session.questions || []).map((q) => ({ question_id: q.id, user_answer: answers[q.id] || '', is_flagged: flagged.has(q.id) }));
    const timeTaken = Math.floor((Date.now() - startedAt.current) / 1000);
    try {
      // v4: the sitting is an exam session. Item 3: it is a Campus sitting with
      // its own submit route. The start response says which by the id it carries.
      const submitUrl = session.sitting_id != null
        ? API_ENDPOINTS.CAMPUS_MY_SITTING_SUBMIT(session.sitting_id)
        : API_ENDPOINTS.KQ_EXAM_SUBMIT(session.session_id);
      const res = await api.postJson(submitUrl, { answers: list, time_taken: timeTaken });
      try { localStorage.removeItem(storeKey(id)); } catch { /* ignore */ }
      if (sittingKey) clearSeq(sittingKey);
      setOutcome({ ...res, submitted_at: res?.submitted_at || new Date().toISOString() });
      // Marks mean self-assessment, with or without the per-question list (the
      // live sitting submit returned marks and no `results`, 17 Sep). Only a
      // payload with no marks at all is the proctored receipt.
      setPhase(Array.isArray(res?.results) || res?.percentage != null ? 'results' : 'receipt');
    } catch (e) {
      // A second submit is not a failure: the 409 carries the outcome that
      // already exists (schema v2 §5). Mode-gated by the server, so proctored
      // brings state and submitted_at only, and never a mark.
      const prior = e.reason === 'sitting_already_submitted' ? (e.body?.data || e.body) : null;
      if (prior && (Array.isArray(prior.results) || prior.percentage != null || prior.submitted_at)) {
        try { localStorage.removeItem(storeKey(id)); } catch { /* ignore */ }
        if (sittingKey) clearSeq(sittingKey);
        setOutcome(prior);
        setPhase(Array.isArray(prior.results) || prior.percentage != null ? 'results' : 'receipt');
      } else if (e.reason === 'sitting_expired' || e.reason === 'already_submitted' || e.reason === 'session_already_submitted' || e.reason === 'sitting_already_submitted') {
        try { localStorage.removeItem(storeKey(id)); } catch { /* ignore */ }
        setError(e.message);
        setPhase('expired');
      } else {
        setError(e.message);
        setPhase('running');
      }
    } finally {
      submitting.current = false;
    }
  }, [api, id, session, answers, flagged, sittingKey]);

  // The sign-in page reads this note (build brief §15): a student whose tab
  // lost its token mid-sitting is told the exam is still open and sent
  // straight back to it after signing in, rather than to a generic dashboard.
  useEffect(() => {
    if (phase === 'running') noteOpenSitting(id, session?.exam_name || assignment?.template_name || '');
    else if (phase === 'receipt' || phase === 'results' || phase === 'expired' || phase === 'blocked') clearOpenSitting(id);
  }, [phase, id, session, assignment]);

  // ── clock ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'running' || timeLeft == null) return undefined;
    const t = setInterval(() => setTimeLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [phase, timeLeft == null]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (phase === 'running' && timeLeft === 0) submit();
  }, [timeLeft, phase, submit]);

  const questions = useMemo(() => session?.questions || [], [session]);
  const q = questions[cur];
  const total = questions.length;
  const answeredCount = useMemo(() => questions.filter((x) => answers[x.id]).length, [questions, answers]);
  const toggleFlag = (qid) => setFlagged((s) => { const n = new Set(s); if (n.has(qid)) n.delete(qid); else n.add(qid); return n; });
  const leave = () => navigate(kq('student'));

  // ── screens ───────────────────────────────────────────────────────
  if (phase === 'starting') {
    return <div className="kqc"><div className="kqc-page"><Loading label="Opening your exam" /></div></div>;
  }

  if (phase === 'blocked' || phase === 'expired') {
    return (
      <div className="kqc kqc-runner">
        <SimpleTop student={student} />
        <div className="kqc-page--center" style={{ maxWidth: 620 }}>
          <div className="kqc-kicker" style={{ marginBottom: 12 }}>{assignment?.template_name || 'Exam'}</div>
          <h1 className="kqc-h1" style={{ marginBottom: 12 }}>{phase === 'expired' ? 'This sitting has been submitted' : 'This exam can’t be started'}</h1>
          <Alert kind={phase === 'expired' ? 'info' : 'error'}>{error}</Alert>
          {phase === 'expired' && <p className="kqc-small" style={{ color: 'var(--color-neutral-800)', marginBottom: 18, maxWidth: '52ch' }}>Auto-submission happens on the server when the clock runs out, so the answers you had given were counted. Your teacher releases the result.</p>}
          <button type="button" className="btn btn-primary" onClick={leave}>Back to my exams</button>
        </div>
      </div>
    );
  }

  if (phase === 'receipt') {
    return (
      <div className="kqc kqc-runner">
        <SimpleTop student={student} />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(24px,5vw,64px) clamp(16px,4vw,44px)' }}>
          <div style={{ width: '100%', maxWidth: 620 }}>
            <span className="kqc-bigcheck"><CheckIcon /></span>
            <h1 className="kqc-h1--big" style={{ marginBottom: 16 }}>Your exam is in</h1>
            <p className="kqc-lede kqc-lede--big" style={{ marginBottom: 30, maxWidth: '44ch', fontSize: 'clamp(16px,1.7vw,19px)' }}>
              {outcome?.submit_reason === 'sitting_expired'
                ? <>The time ran out before this reached the server, so the answers already saved were the ones counted. It was closed at {fmtDateTimeZ(outcome?.submitted_at)}. There is nothing else for you to do.</>
                : <>All {outcome?.question_count ?? total} answers were received at {fmtDateTimeZ(outcome?.submitted_at)}. There is nothing else for you to do.</>}
            </p>
            <div className="kqc-box" style={{ marginBottom: 26 }}>
              <div className="kqc-box__head">Receipt</div>
              <div className="kqc-receipt">
                <div><small>Exam</small><strong>{session?.exam_name || assignment?.template_name}</strong></div>
                <div><small>Questions</small><strong>{outcome?.question_count ?? total} of {total}</strong></div>
                <div><small>Submitted</small><strong>{fmtDateTimeZ(outcome?.submitted_at)}</strong></div>
              </div>
              <div className="kqc-box__body kqc-box__body--surface">
                <div className="kqc-mono" style={{ fontSize: 14, marginBottom: 8 }}>Why there is no score here</div>
                <div className="kqc-small" style={{ lineHeight: 1.55, color: 'var(--color-neutral-800)' }}>This was a proctored sitting. Marks, correct answers and explanations are not sent to your device at all &mdash; your teacher reviews the paper and releases the result. Practice exams work the other way: you see everything the moment you submit.</div>
              </div>
            </div>
            <div className="kqc-row" style={{ gap: 12 }}>
              <button type="button" className="btn btn-primary" style={{ minHeight: 50, fontSize: 15 }} onClick={leave}>Back to my exams</button>
              <button type="button" className="btn btn-secondary" style={{ minHeight: 50 }} onClick={() => { logout(); navigate(kq('login')); }}>Log out</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'results') {
    const pctv = Math.round((Number(outcome.percentage) || 0) * 10) / 10;
    const pos = new Map(questions.map((x, i) => [x.id, i + 1]));
    const textOf = new Map(questions.map((x) => [x.id, x.question_text]));
    const hasReview = Array.isArray(outcome.results) && outcome.results.length > 0;
    // Unanswered questions are absent from `results` (seen live), so count
    // from the marks when they are there.
    const wrong = outcome.total != null && outcome.scored != null
      ? Math.max(0, Number(outcome.total) - Number(outcome.scored))
      : (outcome.results || []).filter((r) => !r.is_correct).length;
    // Verdict from the server, against the threshold snapshotted at start
    // (v4 §6). Null means this paper carries no pass mark.
    const passed = outcome.passed != null ? Boolean(outcome.passed) : null;
    const passMark = outcome.pass_percentage != null ? Number(outcome.pass_percentage) : null;
    return (
      <div className="kqc kqc-runner">
        <SimpleTop student={student} />
        <div className="kqc-page--center" style={{ maxWidth: 860 }}>
          <div className="kqc-kicker" style={{ marginBottom: 10 }}>Practice &middot; {session?.exam_name || assignment?.template_name}</div>
          <div className="kqc-score" style={{ marginBottom: 8 }}>
            <h1 className="kqc-h1--big" style={{ margin: 0 }}>{pctv}%</h1>
            {passed !== null && <span className={`kqc-score__v${passed ? '' : ' is-low'}`}>{passed ? 'PASS' : 'FAIL'}{passMark != null ? ` · ${passMark}% to pass` : ''}</span>}
          </div>
          <p className="kqc-lede kqc-lede--big" style={{ marginBottom: 26 }}>{outcome.scored} of {outcome.total} correct &middot; {wrong} to look at again.{passed === null ? ' This paper has no pass mark, so there is no verdict.' : ''}{hasReview ? ' Your answers are below with the explanations.' : ' The question by question review is not available for this sitting.'}</p>
          <div className="kqc-row" style={{ marginBottom: 28 }}>
            <button type="button" className="btn btn-primary" onClick={leave}>Back to my exams</button>
            <button type="button" className="btn btn-secondary" onClick={() => window.location.reload()}>Sit it again</button>
          </div>
          <div className="kqc-review">
            {(outcome.results || []).map((r, i) => (
              <div key={r.question_id ?? i}>
                <span className={`n${r.is_correct ? '' : ' is-wrong'}`}>Q{pos.get(r.question_id) ?? i + 1}</span>
                <div>
                  <div className="q">{textOf.get(r.question_id)}</div>
                  <div className="a">You: <strong>{r.user_answer || '—'}</strong>{r.is_correct ? ' · correct' : <> &rarr; Correct: <strong>{r.correct_answer}</strong></>}</div>
                  {r.explanation && <div className="e">{r.explanation}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── running / submitting ──────────────────────────────────────────
  const low = timeLeft != null && timeLeft < 300;
  return (
    <div className="kqc kqc-runner">
      <header className="kqc-runner__head">
        <div style={{ minWidth: 0 }}>
          <div className="kqc-runner__title">{session?.exam_name || assignment?.template_name || 'Exam'}</div>
          <div className="kqc-runner__meta">
            <span className={`tag ${proctored ? 'tag-fill' : 'tag-neutral'}`}>{proctored ? 'Proctored' : 'Practice'}</span>
            <span>{proctored ? 'One attempt · no answers shown' : 'Retake any time · answers shown after submit'}</span>
            {/* second line of defence for a client that started from a stale list */}
            {session?.short_window && <span className="kqc-accent-ink">This exam closes before the full time. The clock shows what you have.</span>}
          </div>
        </div>
        <span className="kqc-spacer" />
        {SAVE_LABEL[saveState] && <span className={`kqc-small kqc-savestate is-${saveState}`} role="status">{SAVE_LABEL[saveState]}</span>}
        {timeLeft != null && (
          <div className={`kqc-runner__clock${low ? ' is-low' : ''}`}>
            <small>Time left</small>
            <strong>{fmtClock(timeLeft)}</strong>
          </div>
        )}
        <ThemeToggle />
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setLeaving(true)} disabled={phase === 'submitting'}><XIcon />Leave</button>
        <button type="button" className="btn btn-primary" onClick={() => setConfirm(true)} disabled={phase === 'submitting'}>{phase === 'submitting' ? 'Submitting…' : 'Submit'}</button>
      </header>
      <div className="kqc-progress-line"><i style={{ width: `${total ? (answeredCount / total) * 100 : 0}%` }} /></div>

      <div className="kqc-runner__body">
        <div className="kqc-runner__q">
          {error && <Alert kind="error" onClose={() => setError('')}>{error} Your answers are still here — try submitting again.</Alert>}
          {q ? (
            <>
              <div className="kqc-row" style={{ marginBottom: 18 }}>
                <span className="kqc-kicker">Question {cur + 1} of {total}</span>
                {q.module && <span className="tag tag-neutral">{q.module}</span>}
              </div>
              <h1>{q.question_text}</h1>
              <div className="kqc-options" role="radiogroup" aria-label={`Question ${cur + 1}`}>
                {(q.options || []).map((opt, i) => {
                  const o = optionOf(opt, i);
                  const on = answers[q.id] === o.letter;
                  return (
                    <button type="button" key={o.letter} role="radio" aria-checked={on} className={`kqc-option${on ? ' is-selected' : ''}`} onClick={() => setAnswers((a) => ({ ...a, [q.id]: o.letter }))}>
                      <span className="kqc-option__letter">{o.letter}</span>
                      <span className="kqc-option__text">{o.text}</span>
                    </button>
                  );
                })}
              </div>
              <div className="kqc-runner__nav">
                <button type="button" className="btn btn-secondary" style={{ minHeight: 48 }} disabled={cur === 0} onClick={() => setCur((c) => c - 1)}>&larr; Previous</button>
                <button type="button" className="btn btn-primary" style={{ minHeight: 48 }} disabled={cur >= total - 1} onClick={() => setCur((c) => c + 1)}>Next question &rarr;</button>
                <span className="kqc-spacer" />
                <button type="button" className={`kqc-flagbtn${flagged.has(q.id) ? ' is-on' : ''}`} onClick={() => toggleFlag(q.id)} aria-pressed={flagged.has(q.id)}><FlagIcon />{flagged.has(q.id) ? 'Flagged' : 'Flag for review'}</button>
              </div>
            </>
          ) : <div className="kqc-empty">This paper has no questions.</div>}
        </div>

        <aside className="kqc-runner__side">
          <div className="kqc-kicker kqc-kicker--dim" style={{ marginBottom: 14 }}>Your progress</div>
          <div className="kqc-qgrid">
            {questions.map((x, i) => (
              <button type="button" key={x.id} className={`${i === cur ? 'is-current' : answers[x.id] ? 'is-answered' : ''}${flagged.has(x.id) ? ' is-flagged' : ''}`} onClick={() => setCur(i)} aria-label={`Go to question ${i + 1}`}>{i + 1}</button>
            ))}
          </div>
          <div className="kqc-legend">
            <div><i className="is-answered" />Answered &middot; {answeredCount}</div>
            <div><i className="is-flagged" />Flagged &middot; {flagged.size}</div>
            <div><i />Not answered &middot; {total - answeredCount}</div>
          </div>
          <p className="kqc-small kqc-muted" style={{ marginTop: 18 }}>Your answers are saved as you go. If the connection drops you can resume where you stopped — the clock is kept on the server.</p>
        </aside>
      </div>

      {confirm && (
        <Dialog title={proctored ? 'Submit — this is your one attempt' : 'Submit this practice paper'} danger={proctored} icon={proctored ? <WarnIcon /> : <CheckIcon />} onClose={() => setConfirm(false)}>
          <h3>{answeredCount} of {total} answered{flagged.size ? ` · ${flagged.size} flagged` : ''}</h3>
          <p className="kqc-small" style={{ color: 'var(--color-neutral-800)', marginBottom: 16 }}>
            {proctored ? 'Once submitted you cannot open this exam again, and no score is shown here — your teacher releases the result.' : 'You will see your score, the correct answers and the explanations straight away, and you can sit it again whenever you like.'}
            {answeredCount < total ? ` ${total - answeredCount} unanswered question${total - answeredCount === 1 ? '' : 's'} will be marked as blank.` : ''}
          </p>
          <div className="kqc-row">
            <button type="button" className="btn btn-primary" onClick={submit}>Submit now</button>
            <button type="button" className="btn btn-secondary" onClick={() => setConfirm(false)}>Keep working</button>
          </div>
        </Dialog>
      )}

      {leaving && (
        <Dialog title="Leave this sitting?" icon={<WarnIcon />} onClose={() => setLeaving(false)}>
          <h3>{proctored ? 'You can come back and finish this sitting' : 'Your answers stay where they are'}</h3>
          <p className="kqc-small" style={{ color: 'var(--color-neutral-800)', marginBottom: 16 }}>
            {proctored
              ? 'You can come back and finish this sitting — leaving does not use up your one attempt. The clock keeps running while you are away, and if it runs out the exam is submitted with whatever you have answered.'
              : 'This is a practice paper. Come back any time; the clock keeps running while you are away, and you can always sit it again.'}
          </p>
          <div className="kqc-row">
            <button type="button" className="btn btn-primary" onClick={leave}>Leave for now</button>
            <button type="button" className="btn btn-secondary" onClick={() => setLeaving(false)}>Keep working</button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

const SimpleTop = ({ student }) => (
  <header className="kqc-topbar">
    <span className="kqc-topbar__mark"><Mark /></span>
    <span className="kqc-topbar__brand">Kuestion</span>
    <span className="kqc-spacer" />
    {/* Only on the screens around a sitting, never the running one. */}
    <AppNoticeboard />
    <ThemeToggle />
    <span className="kqc-small kqc-muted">{student?.login_id}</span>
  </header>
);
